import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

type ConsentStatus = "accepted" | "declined" | "pending";

const STORAGE_KEY = "ironhaven_cookie_consent";

export default function CookieConsentBanner() {
  const [status, setStatus] = useState<ConsentStatus>("pending");
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "accepted" || stored === "declined") {
      setStatus(stored);
      setVisible(false);
    } else {
      // Slight delay so page loads first, then banner slides in
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = (choice: "accepted" | "declined") => {
    setLeaving(true);
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, choice);
      setStatus(choice);
      setVisible(false);
      setLeaving(false);
    }, 380);
  };

  if (!visible || status !== "pending") return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-500 ease-out ${
        leaving ? "translate-y-full" : "translate-y-0"
      }`}
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="false"
    >
      {/* Backdrop gradient so text is readable over any page */}
      <div className="bg-gray-900 border-t border-gray-700 shadow-2xl">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">

            {/* Icon + text */}
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-9 h-9 flex items-center justify-center shrink-0 bg-amber-500/10 rounded-lg mt-0.5">
                <i className="ri-cookie-line text-xl text-amber-400"></i>
              </div>
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm mb-1">
                  We use cookies &amp; tracking technologies
                </p>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Iron Haven Supply uses cookies to keep your cart working, analyze site traffic, and deliver relevant ads through
                  Google Ads and Meta. By clicking <strong className="text-gray-300">Accept All</strong>, you consent to our use
                  of all cookies.{" "}
                  <Link
                    to="/cookie-policy"
                    className="text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors"
                  >
                    Cookie Policy
                  </Link>{" "}
                  &amp;{" "}
                  <Link
                    to="/privacy-policy"
                    className="text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </p>

                {/* Expandable details */}
                <button
                  type="button"
                  onClick={() => setShowDetails((p) => !p)}
                  className="mt-2 text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className={`ri-arrow-${showDetails ? "up" : "down"}-s-line text-sm`}></i>
                  </div>
                  {showDetails ? "Hide details" : "What do we use cookies for?"}
                </button>

                {showDetails && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { icon: "ri-shield-check-line", color: "text-emerald-400", label: "Essential", desc: "Cart, checkout & security — always on" },
                      { icon: "ri-bar-chart-line", color: "text-sky-400", label: "Analytics", desc: "Google Analytics — page views & behavior" },
                      { icon: "ri-focus-3-line", color: "text-violet-400", label: "Advertising", desc: "Google Ads & Meta — remarketing & conversions" },
                      { icon: "ri-settings-3-line", color: "text-amber-400", label: "Functional", desc: "Preferences & recently viewed products" },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-2 bg-gray-800/60 rounded-lg px-3 py-2">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                          <i className={`${item.icon} text-sm ${item.color}`}></i>
                        </div>
                        <div>
                          <p className="text-white text-xs font-semibold">{item.label}</p>
                          <p className="text-gray-400 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
              <button
                type="button"
                onClick={() => dismiss("declined")}
                className="text-gray-400 hover:text-white text-xs font-medium px-4 py-2 rounded-md border border-gray-700 hover:border-gray-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Essential Only
              </button>
              <button
                type="button"
                onClick={() => dismiss("accepted")}
                className="bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold px-5 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Accept All
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
